import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  UK_CONSENT_CATEGORIES,
  UK_CONSENT_CATEGORY_LABELS,
  type UkConsentCategory,
} from '@4cm/core/auth';
import { ConsentLayout } from './ConsentLayout';
import { getConsentDraft } from '../../../lib/consent/consentDraft';
import { commitParentalConsent } from '../../../lib/consent/commitConsent';

/**
 * Step 6 — granular consent ticks (parent-side).
 *
 * Six categories. None pre-checked (ICO §7 + §13 — pre-checked
 * boxes are a forbidden dark pattern). `account_creation` is required
 * — without it there's nothing to consent to. The other five are
 * independent.
 *
 * Submit calls the commit-parental-consent edge function which
 * creates the parent's auth row + writes one consent_log row per
 * ticked category. On success, routes to Step 7 confirmation.
 */
export function GranularConsents() {
  const navigate = useNavigate();
  const [draft] = useState(() => getConsentDraft());
  const [verified] = useState(
    () => sessionStorage.getItem('4cm_consent_verified') === 'true',
  );
  const [picks, setPicks] = useState<Record<UkConsentCategory, boolean>>(
    () => Object.fromEntries(
      UK_CONSENT_CATEGORIES.map((c) => [c, c === 'account_creation']),
    ) as Record<UkConsentCategory, boolean>,
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Guard: must have completed prior steps + verification.
  useEffect(() => {
    if (!draft?.parentEmail || !draft?.childDob) {
      navigate('/parent/consent/dob', { replace: true });
      return;
    }
    if (!verified) {
      navigate('/parent/consent/yoti', { replace: true });
    }
  }, [draft, verified, navigate]);

  if (!draft) return null;

  const accountCreationOff = !picks.account_creation;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (accountCreationOff) {
      setError('Account creation is required to use 4CM.');
      return;
    }
    const supabaseUrl = (import.meta.env.VITE_SUPABASE_URL as string | undefined) ?? '';
    const anonKey = (import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined) ?? '';
    if (!draft.parentEmail || !draft.childDob) {
      setError('Some required information is missing. Please start over.');
      return;
    }
    setSubmitting(true);
    setError(null);
    const consents = UK_CONSENT_CATEGORIES.filter((c) => picks[c]);
    const result = await commitParentalConsent({
      supabaseUrl,
      anonKey,
      parentEmail: draft.parentEmail,
      childDob: draft.childDob,
      consents,
      inviteToken: draft.inviteToken,
      verified: true,
      noticeVersion: 'v1.0',
    });
    setSubmitting(false);
    if (!result.ok) {
      setError(result.error ?? 'Could not save your consents. Please try again.');
      return;
    }
    // Stash the parent_user_id for the confirmation screen.
    sessionStorage.setItem('4cm_consent_parent_user_id', result.parentUserId ?? '');
    sessionStorage.setItem(
      '4cm_consent_count',
      String(result.consentCount ?? consents.length),
    );
    navigate('/parent/consent/done');
  };

  return (
    <ConsentLayout
      step={6}
      title="What can your child do on 4CM?"
      lead={
        <>
          Tick what you're OK with. Each one is separate — you can come back and change any of these later in Settings.
          {' '}<strong>None are ticked to start.</strong> Read more about what each means in our{' '}
          <Link to="/legal/privacy" target="_blank" rel="noreferrer">privacy notice</Link>.
        </>
      }
    >
      <form className="consent-form" onSubmit={(e) => void handleSubmit(e)} noValidate>
        <ul className="consent-picks">
          {UK_CONSENT_CATEGORIES.map((cat) => {
            const meta = UK_CONSENT_CATEGORY_LABELS[cat];
            return (
              <li key={cat} className="consent-picks__row">
                <label className="consent-picks__label">
                  <input
                    type="checkbox"
                    checked={picks[cat]}
                    onChange={(e) => setPicks((p) => ({ ...p, [cat]: e.target.checked }))}
                    disabled={submitting || meta.required}
                    aria-describedby={`consent-${cat}-sub`}
                  />
                  <div className="consent-picks__copy">
                    <div className="consent-picks__title">
                      {meta.label}
                      {meta.required && <span className="consent-picks__required"> · required</span>}
                    </div>
                    <div className="consent-picks__sub" id={`consent-${cat}-sub`}>
                      {meta.sub}
                    </div>
                  </div>
                </label>
              </li>
            );
          })}
        </ul>

        {error && <p className="consent-form__error">{error}</p>}

        <div className="consent-form__actions">
          <button
            type="submit"
            className="consent-form__btn"
            disabled={accountCreationOff || submitting}
          >
            {submitting ? 'Saving…' : 'Confirm and finish'}
          </button>
          <button
            type="button"
            className="consent-form__btn consent-form__btn--ghost"
            onClick={() => navigate('/parent/consent/yoti')}
            disabled={submitting}
          >
            Back
          </button>
        </div>
      </form>
    </ConsentLayout>
  );
}
